import SectionHero from '../Layouts/SectionHero';
import React, { useState, useEffect, useRef } from 'react';
import useServerQuery from "../FetchServerStats";
import { DONATE_CONFIG } from '../../config';
import styles from './Donate.module.css';
import { Coins, Heart, ChevronDown } from 'lucide-react';
import StoreCheckout, { SupportCheckout } from './StoreCheckout';
import checkout from './StoreCheckout.module.css';

const DonatePage = () => {
  const categories = DONATE_CONFIG.categories;
  const [selectedCategoryIdx, setSelectedCategoryIdx] = useState(0);
  const [selectedItemKey, setSelectedItemKey] = useState(categories[0].items[0].key);
  const [months, setMonths] = useState(1);
  const [loading, setLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  const selectCategoryByIndex = (idx) => {
    setPaymentError('');
    setSelectedCategoryIdx(idx);
    // При смене категории гарантируем, что выбранная карточка относится к новой категории.
    const firstKey = categories?.[idx]?.items?.[0]?.key;
    if (firstKey) setSelectedItemKey(firstKey);
  };

  // Никнейм глобально для всех форм
  const getInitialNickname = () => {
    try {
      return localStorage.getItem('iw_nickname') || '';
    } catch {
      return '';
    }
  };
  const [nickname, setNickname] = useState(getInitialNickname());
  useEffect(() => {
    try {
      if (nickname) localStorage.setItem('iw_nickname', nickname);
    } catch {}
  }, [nickname]);

  const { fetchData: fetchServerStats, data: serverStats } = useServerQuery();
  const lastCheckedNickRef = useRef("");
  const [nickCheckError, setNickCheckError] = useState("");

  const faqItems = [
    {
      id: 1,
      question: 'Как быстро на счёт приходят IW-Токены?',
      answer: 'IW-Токены зачисляются на баланс автоматически в течение 1-5 минут после успешной оплаты. Вы получите уведомление в игре и на сайте.'
    },
    {
      id: 2,
      question: 'Можно ли вернуть деньги?',
      answer: 'Возврат средств возможен в течение 72 часов после покупки, если IW-Токены не были потрачены. Подробности в публичной оферте.'
    },
    {
      id: 3,
      question: 'Что делать, если IW-Токены не начислились?',
      answer: 'Если IW-Токены не были зачислены в течение 15 минут, свяжитесь с нами через страницу контактов. Приложите скриншот подтверждения платежа.'
    },
    {
      id: 4,
      question: 'Можно ли купить IW-Токены в подарок?',
      answer: 'Да, при покупке вы можете указать никнейм другого игрока. IW-Токены будут зачислены на баланс указанного аккаунта.'
    }
  ];

  const toggleFAQ = (id) => {
    setExpandedFAQ(expandedFAQ === id ? null : id);
  };

  const handlePaymentRequest = async (payload) => {
    setLoading(true);
    setPaymentError('');
    try {
      const response = await fetch(DONATE_CONFIG.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': DONATE_CONFIG.apiKey
        },
        body: JSON.stringify(payload)
      });
      const paymentUrl = await response.text();
      if (response.ok && paymentUrl) {
        window.location.href = paymentUrl;
      } else {
        setPaymentError('Не удалось создать платёж. Попробуйте ещё раз или обратитесь в поддержку.');
      }
    } catch (error) {
      setPaymentError('Не удалось подключиться к оплате. Проверьте интернет и попробуйте ещё раз.');
    } finally {
      setLoading(false);
    }
  };

  const DonateFormInner = ({ optionData, nickname, setNickname }) => {
    const [formData, setFormData] = useState({
      hexColor: optionData.fields.includes('hexColor') ? '#f7bc45' : '',
      tag: ''
    });
    const [count, setCount] = useState(1);
    // Локальный никнейм для формы
      const [localNick, setLocalNick] = useState(() => localStorage.getItem('iw_nickname') || '');

    const totalSum = optionData.pricePerMonth
      ? (optionData.pricePerMonth * months).toFixed(2)
      : (optionData.price * count).toFixed(2);

    const checkNicknameOnline = async (nick) => {
      setNickCheckError("");
      if (!nick) return false;
      if (lastCheckedNickRef.current === nick && serverStats) {
        return isNickOnline(nick, serverStats);
      }
      await fetchServerStats();
      lastCheckedNickRef.current = nick;
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(isNickOnline(nick, serverStats));
        }, 300);
      });
    };

    const isNickOnline = (nick, stats) => {
      if (!stats || !Array.isArray(stats.Players)) return false;
      return stats.Players.some(
        (p) => typeof p.Name === 'string' && p.Name.toLowerCase() === nick.trim().toLowerCase()
      );
    };

    const onSubmit = async (e) => {
      e.preventDefault();
      if ((['lootbox', 'xp_boost'].includes(optionData.type)) && optionData.fields.includes('nickname')) {
        const ok = await checkNicknameOnline(localNick);
        if (!ok) {
          setNickCheckError('Для этой покупки игрок с этим ником должен находиться на сервере.');
          return;
        }
      }
      setNickCheckError("");
      handlePaymentRequest({
        sum: totalSum,
        type: optionData.type,
        nickname: localNick,
        hexColor: optionData.fields.includes('hexColor') ? formData.hexColor : "",
        tag: optionData.fields.includes('tag') ? formData.tag : "",
        durationMonths: optionData.pricePerMonth ? months : undefined,
        lootboxId: optionData.lootboxId,
        xpBoostId: optionData.xpBoostId,
        count: !optionData.pricePerMonth ? count : undefined,
      });
    };

    return (
      <form onSubmit={onSubmit} className={styles.form}>
        {optionData.pricePerMonth && (
          <div className={styles.durationPicker}>
            <label>Срок подписки:</label>
            <select value={months} onChange={(e) => setMonths(parseInt(e.target.value))}>
              <option value={1}>1 месяц</option>
              <option value={3}>3 месяца</option>
              <option value={6}>6 месяцев</option>
              <option value={12}>1 год</option>
            </select>
          </div>
        )}

        {/* Для лутбоксов — инпут количества */}
        {!optionData.pricePerMonth && (
          <div className={styles.durationPicker}>
            <label>Количество:</label>
            <input
              type="number"
              min={1}
              max={99}
              value={count}
              onChange={e => setCount(Math.max(1, Math.min(99, Number(e.target.value))))}
              className={styles.inputField}
              style={{ width: 80, display: 'inline-block', marginLeft: 8 }}
            />
          </div>
        )}

        {optionData.fields.includes('nickname') && (
          <input
            placeholder="Никнейм"
            className={styles.inputField}
            required
            value={localNick}
            onChange={e => {
              setLocalNick(e.target.value);
              try { localStorage.setItem('iw_nickname', e.target.value); } catch {}
            }}
          />
        )}

        {nickCheckError && (
          <div style={{ color: 'red', marginBottom: 8 }}>{nickCheckError}</div>
        )}

        {optionData.fields.includes('hexColor') && (
          <div className={styles.colorGroup}>
            <label>Цвет:</label>
            <input type="color" className={styles.colorPicker}
              value={formData.hexColor} onChange={e => setFormData({...formData, hexColor: e.target.value})} />
          </div>
        )}

        {optionData.fields.includes('tag') && (
          <input placeholder="Ваш тег" className={styles.inputField} required
            value={formData.tag} onChange={e => setFormData({...formData, tag: e.target.value})} />
        )}

        <button type="submit" disabled={loading} className="btn-primary-custom">
          {loading ? 'Загрузка...' : `Оплатить ${totalSum} ₽`}
        </button>
      </form>
    );
  };

  const renderForm = (optionKey, option) => {
    if (option.type === 'donation') {
      return <SupportCheckout optionData={option} loading={loading} onPayment={handlePaymentRequest} />;
    }
    return <DonateFormInner optionData={option} nickname={nickname} setNickname={setNickname} />;
  };

  return (
    <div id="buy-status" className={styles.page}>
      {/* ——— Hero ——— */}
      <SectionHero
        eyebrow="Поддержи мир InterWorld"
        title="Магазин."
        highlight="Больше возможностей."
        description="Пополняй IW-Токены, выбирай своё и помогай любимому миру становиться ещё лучше."
      />

      <div className={styles.body}>
        <section className={checkout.panel} aria-label="Покупка и поддержка проекта">
          <div className={checkout.navigation} role="group" aria-label="Раздел магазина">
            {categories.map((cat, idx) => (
              <button
                key={cat.key}
                type="button"
                className={checkout.categoryButton}
                aria-pressed={idx === selectedCategoryIdx}
                disabled={loading}
                onClick={() => selectCategoryByIndex(idx)}
              >
                {cat.key === 'support' ? <Heart size={18} strokeWidth={1.8} aria-hidden="true" /> : <Coins size={18} strokeWidth={1.8} aria-hidden="true" />}
                {cat.title}
              </button>
            ))}
          </div>

          {/* Описание категории */}
          <div className={styles.categorySection}>
            {!['donate_currency', 'support'].includes(categories[selectedCategoryIdx].key) && categories[selectedCategoryIdx].description && (
              <div
                className={checkout.description}
                dangerouslySetInnerHTML={{
                  __html: categories[selectedCategoryIdx].description.replace(/(https?:\/\/\S+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>')
                }}
              />
            )}
            {categories[selectedCategoryIdx].key === 'donate_currency' ? (
              <StoreCheckout packages={categories[selectedCategoryIdx].items} loading={loading} onPayment={handlePaymentRequest} paymentError={paymentError} />
            ) : categories[selectedCategoryIdx].key === 'support' ? (
              <SupportCheckout optionData={categories[selectedCategoryIdx].items[0]} loading={loading} onPayment={handlePaymentRequest} paymentError={paymentError} />
            ) : (
              <div className={styles.pricingGrid}>
                {categories[selectedCategoryIdx].items.map((item) => {
                  const isSelected = selectedItemKey === item.key;
                  const price = item.pricePerMonth || item.price;
                  const isCustomAmount = item.type === 'donation' || item.customAmount;
                  return (
                    <div
                      key={item.key}
                      className={`${styles.priceCard} ${isSelected ? styles.selectedCard : ''} ${item.isFeatured ? styles.featuredCard : ''}`}
                      onClick={() => setSelectedItemKey(item.key)}
                    >
                      <div className={styles.priceTag}>
                        {!isCustomAmount && (
                          <>
                            {price}
                            <span className={styles.currency}> {item.priceUnit || '₽'}</span>
                          </>
                        )}
                      </div>

                      <h2 className={styles.cardTitle}>{item.title}</h2>
                      <p className={styles.cardDescription}>{item.description}</p>

                      <ul className={styles.featureList}>
                        {item.features.map((f, i) => <li key={i}>{f}</li>)}
                      </ul>

                      {isSelected ? renderForm(item.key, item) : <button className={styles.selectButton}>Выбрать</button>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {paymentError && !['donate_currency', 'support'].includes(categories[selectedCategoryIdx].key) && <p className={checkout.error} role="alert">{paymentError}</p>}
        </section>

        {/* FAQ Секция */}
        <div className={styles.faqSection}>
          <h2 className={styles.faqTitle}>Часто задаваемые вопросы</h2>
          <div className={styles.faqContainer}>
            {faqItems.map((item) => (
              <div key={item.id} className={styles.faqItem}>
                <button
                  className={`${styles.faqQuestion} ${expandedFAQ === item.id ? styles.expanded : ''}`}
                  onClick={() => toggleFAQ(item.id)}
                  aria-expanded={expandedFAQ === item.id}
                  aria-controls={`store-faq-${item.id}`}
                >
                  <span>{item.question}</span>
                  <ChevronDown size={18} className={styles.faqIcon} aria-hidden="true" />
                </button>
                {expandedFAQ === item.id && (
                  <div id={`store-faq-${item.id}`} className={styles.faqAnswer}>
                    {item.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonatePage;
